import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Info, Target, Code, BookOpen, Mail, CheckCircle2, Clock } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";

export default function About() {
  const { t } = useTranslation();

  const journey = [1, 2, 3, 4, 5, 6, 7, 8].map((n) => t(`about.journey_${n}`));
  const tech = [1, 2, 3, 4, 5].map((n) => t(`about.tech_${n}`));
  const done = [1, 2, 3].map((n) => t(`about.state_done_${n}`));
  const todo = [1, 2, 3].map((n) => t(`about.state_todo_${n}`));

  return (
    <div className="relative min-h-screen hero-radial grain">
      <header className="relative z-30 flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/" className="flex items-center gap-2 text-stone-300 hover:text-amber-200" data-testid="back-home">
          <ChevronLeft className="w-4 h-4" /> {t("common.back_home")}
        </Link>
        <div className="flex items-center gap-2 font-serif text-lg">
          <Info className="w-5 h-5 text-teal-300" /> {t("about.title")}
        </div>
        <LanguageSwitcher />
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-6 sm:px-10 pb-20 text-stone-200" data-testid="about-content">
        <section className="pt-4 pb-10 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-amber-300 font-bold">{t("about.kicker")}</div>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-black text-stone-50">{t("about.title")}</h1>
          <p className="mt-6 text-lg text-stone-300 leading-relaxed max-w-2xl mx-auto italic">{t("about.lead")}</p>
        </section>

        <section className="space-y-4 py-8 border-t border-white/5" data-testid="about-story">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-stone-50">{t("about.story_h")}</h2>
          <p className="leading-relaxed">{t("about.story_p1")}</p>
          <p className="leading-relaxed">{t("about.story_p2")}</p>
        </section>

        <section className="space-y-6 py-8 border-t border-white/5" data-testid="about-mission">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-stone-50 flex items-center gap-2">
            <Target className="w-6 h-6 text-amber-300" /> {t("about.mission_h")}
          </h2>
          {[1, 2, 3].map((n) => (
            <div key={n} className="glass rounded-2xl p-5 space-y-2" data-testid={`about-mission-${n}`}>
              <h3 className="font-serif text-lg font-bold text-amber-200">{t(`about.mission_${n}_h`)}</h3>
              <p className="text-sm leading-relaxed text-stone-300">{t(`about.mission_${n}_p`)}</p>
            </div>
          ))}
        </section>

        <section className="space-y-4 py-8 border-t border-white/5" data-testid="about-journey">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-stone-50 flex items-center gap-2">
            <Clock className="w-6 h-6 text-teal-300" /> {t("about.journey_h")}
          </h2>
          <ol className="space-y-3 text-sm">
            {journey.map((text, i) => (
              <li key={i} className="flex gap-3 glass rounded-xl p-4" data-testid={`about-journey-${i + 1}`}>
                <span className="shrink-0 w-7 h-7 rounded-full bg-amber-400/20 text-amber-300 font-bold flex items-center justify-center text-xs">{i + 1}</span>
                <span className="leading-relaxed text-stone-300">{text}</span>
              </li>
            ))}
          </ol>
        </section>

        <section className="space-y-4 py-8 border-t border-white/5" data-testid="about-tech">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-stone-50 flex items-center gap-2">
            <Code className="w-6 h-6 text-fuchsia-300" /> {t("about.tech_h")}
          </h2>
          <ul className="space-y-2 text-sm">
            {tech.map((text, i) => (
              <li key={i} className="flex gap-3" data-testid={`about-tech-${i + 1}`}>
                <span className="text-fuchsia-300 shrink-0">•</span>
                <span className="leading-relaxed text-stone-300">{text}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="space-y-6 py-8 border-t border-white/5" data-testid="about-state">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-stone-50 flex items-center gap-2">
            <CheckCircle2 className="w-6 h-6 text-emerald-300" /> {t("about.state_h")}
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="glass rounded-2xl p-5 space-y-3" data-testid="about-state-done">
              <h3 className="font-serif text-base font-bold text-emerald-300">✅ {t("about.state_done_h")}</h3>
              <ul className="space-y-1.5 text-sm text-stone-300">
                {done.map((text, i) => (<li key={i} className="leading-relaxed">— {text}</li>))}
              </ul>
            </div>
            <div className="glass rounded-2xl p-5 space-y-3" data-testid="about-state-todo">
              <h3 className="font-serif text-base font-bold text-amber-300">🚧 {t("about.state_todo_h")}</h3>
              <ul className="space-y-1.5 text-sm text-stone-300">
                {todo.map((text, i) => (<li key={i} className="leading-relaxed">— {text}</li>))}
              </ul>
            </div>
          </div>
        </section>

        <section className="space-y-3 py-8 border-t border-white/5" data-testid="about-future">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-stone-50 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-amber-300" /> {t("about.future_h")}
          </h2>
          <p className="leading-relaxed text-stone-300">{t("about.future_p")}</p>
        </section>

        <section className="mt-10 rounded-3xl glass-strong p-6 sm:p-8 text-center" data-testid="about-contact-cta">
          <h3 className="font-serif text-xl sm:text-2xl font-bold text-stone-50">{t("about.contact_cta_h")}</h3>
          <p className="mt-2 text-sm text-stone-300">{t("about.contact_cta_p")}</p>
          <Link to="/contact">
            <Button className="mt-5 bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold rounded-full px-6" data-testid="about-contact-btn">
              <Mail className="w-4 h-4 mr-2" /> {t("about.contact_cta_btn")}
            </Button>
          </Link>
        </section>
      </main>
    </div>
  );
}
